import { InboxEntryAgentDelegation } from './agentDelegation';
import type { AgentRunSummary } from './agentRunHistory';
import { ChatReplyUnitScope } from './chatReplyUnitScope.enum';
import type { DoBoxWaitingOn, DoBoxWorkKind } from './doBoxSignals';
import type { ExecutorAttention } from './doboxExecutor';
import { ApprovalDecision } from './flowExecution';
import { MessagingPlatform } from './messagingPlatform.enum';
import { Participant } from './participant';
import type { PreparedOutcome, PreparedPlanState } from './prepareDoBoxPlan';
import type { TicketArtifact } from './ticketArtifacts';
import { UserCommunication } from './userCommunication';

export interface InboxEntryChatActivity {
  unitKey: string;
  platform: MessagingPlatform;
  scope: ChatReplyUnitScope;
  /** The entry's own linked messageIds that resolve to this unit, oldest first. */
  linkedMessageIds: string[];
  newReplyMessageIds: string[];
  /** Oldest-first, excluding messages already linked to the entry. */
  messages: UserCommunication[];
  /** True when the viewer is not the owner and the unit is a direct conversation; messages is then empty. */
  messagesHidden: boolean;
  truncated: boolean;
}

export interface InboxEntryNewReply {
  /** An email threadMessageId, or a `chat:` unit key. */
  unitKey: string;
  /** Newest first. */
  messageIds: string[];
}

export enum InboxEntrySource {
  MANUAL = 'MANUAL',
  AGENT = 'AGENT',
  RECURRENCE = 'RECURRENCE',
  INTEGRATION = 'INTEGRATION',
}

/** Why a communication is leaving its inbox entries — the two callers want opposite things. */
export enum CommunicationRemovalReason {
  /** The message row is gone (deleted, or the account was disconnected). Strip it from
   * every entry, resolved ones included, and never destroy the entry itself: a whole
   * team's DoBox must not disappear because one member's integration dropped. */
  MESSAGE_DELETED = 'MESSAGE_DELETED',
  /** The user trashed or spammed the message, so an entry left with nothing on it is
   * theirs to lose. */
  TRASHED_OR_SPAMMED = 'TRASHED_OR_SPAMMED',
}

export enum DoBoxStatusFilter {
  OPEN = 'open',
  RESOLVED = 'resolved',
  SUGGESTION = 'suggestion',
  ALL = 'all',
}

export enum DoBoxEntryKind {
  TASK = 'task',
  SUGGESTION = 'suggestion',
  APPROVAL = 'approval',
}

/** Stable bucketKey values for the non-subgroup buckets of `getInitialDoBoxEntries`; a subgroup bucket's key is its own `taskSubgroupId`. */
export enum DoBoxSpecialBucketKey {
  BACKLOG = '__backlog__',
  SUGGESTIONS = '__suggestions__',
  APPROVALS = '__approvals__',
}

/** GraphQL-facing filter discriminator for the three backlog buckets; separate from `DoBoxSpecialBucketKey` because that enum's `__`-prefixed values aren't valid GraphQL enum value names. */
export enum DoBoxBacklogBucketKind {
  BACKLOG = 'BACKLOG',
  SUGGESTIONS = 'SUGGESTIONS',
  APPROVALS = 'APPROVALS',
}

export interface FlowApprovalRef {
  executionId: string;
  flowId: string;
  ownerUserId: string;
  /** Composite `agentId::order` string identifying the paused APPROVAL_GATE step. */
  pauseStepId: string;
}

/** The flow run that created an entry. */
export interface OriginFlowRef {
  flowId: string;
  executionId?: string;
}

/** Read-side view of the creator flow, resolved per viewer. */
export interface InboxEntryOriginFlow {
  /** Null when the viewer cannot open the flow. */
  flowId: string | null;
  /** Null when the flow no longer exists; empty for a live flow nobody named. */
  name: string | null;
}

export enum InboxEntryPriority {
  URGENT = 0,
  HIGH = 1,
  MEDIUM = 2,
  LOW = 3,
}

export enum InboxEntrySortField {
  CREATED_AT = 'CREATED_AT',
  UPDATED_AT = 'UPDATED_AT',
  DUE_DATE = 'DUE_DATE',
  PRIORITY = 'PRIORITY',
  MANUAL = 'MANUAL',
}

export enum InboxEntrySortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum InboxEntryDueDateFilter {
  NO_DATES = 'NO_DATES',
  OVERDUE = 'OVERDUE',
  DUE_NEXT_DAY = 'DUE_NEXT_DAY',
  DUE_NEXT_WEEK = 'DUE_NEXT_WEEK',
  DUE_NEXT_MONTH = 'DUE_NEXT_MONTH',
}

export enum InboxEntryPriorityFilter {
  NO_PRIORITY = 'NO_PRIORITY',
  URGENT = 'URGENT',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum InboxEntryCardStatusFilter {
  RESOLVED = 'RESOLVED',
  UNRESOLVED = 'UNRESOLVED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ALL = 'ALL',
}

export const APPROVAL_DECISION_CARD_STATUS_FILTERS: ReadonlySet<InboxEntryCardStatusFilter> =
  new Set([
    InboxEntryCardStatusFilter.APPROVED,
    InboxEntryCardStatusFilter.REJECTED,
  ]);

export interface InboxEntryFilters {
  labelIds?: string[];
  showNoLabels?: boolean;
  dueDateFilters?: InboxEntryDueDateFilter[];
  priorityFilters?: InboxEntryPriorityFilter[];
  cardStatusFilter?: InboxEntryCardStatusFilter;
  memberIds?: string[];
  showNoMembers?: boolean;
  unreadOnly?: boolean;
  taskSubgroupId?: string;
  showNoTaskSubgroup?: boolean;
  backlogBucketKind?: DoBoxBacklogBucketKind;
}

export interface InboxEntrySort {
  field: InboxEntrySortField;
  order: InboxEntrySortOrder;
}

export interface ChecklistSubtask {
  id: string;
  title: string;
  completed: boolean;
}

export interface Checklist {
  id: string;
  title: string;
  subtasks: ChecklistSubtask[];
}

export enum ChecklistMutationType {
  ADD_CHECKLIST = 'ADD_CHECKLIST',
  REMOVE_CHECKLIST = 'REMOVE_CHECKLIST',
  RENAME_CHECKLIST = 'RENAME_CHECKLIST',
  ADD_SUBTASK = 'ADD_SUBTASK',
  REMOVE_SUBTASK = 'REMOVE_SUBTASK',
  RENAME_SUBTASK = 'RENAME_SUBTASK',
  TOGGLE_SUBTASK = 'TOGGLE_SUBTASK',
}

export interface ChecklistMutation {
  type: ChecklistMutationType;
  checklistId?: string;
  subtaskId?: string;
  title?: string;
  generatedId?: string;
}

export type InboxEntry = {
  // Primary keys
  taskGroupId: string; // PK — partition key for the InboxEntries table
  id: string; // SK

  // Creator
  createdFor: string; // Creator's userId

  /** Internal link for monitor attention tasks; notifications follow the monitor policy. */
  monitorId?: string;

  // Group metadata
  participantIds: string[]; // List of userIds for all participants
  participants?: Participant[];

  // Shared data
  createdAt?: number;
  subject?: string;
  description?: string;
  communicationIds?: string[];
  communications?: Partial<UserCommunication>[];
  /** Derived, never persisted: the units holding a reply the owner neither answered nor dismissed. */
  newReplies?: InboxEntryNewReply[];
  /** Derived, only filled by `getDetailedInboxEntry(includeChatActivity)`, never persisted. */
  chatActivity?: InboxEntryChatActivity[];
  isSuggestion?: boolean;

  // Task group details
  taskLabelIds?: string[];
  taskSubgroupId?: string | null;
  orderKey?: string;
  checklists?: Checklist[];
  /** Ids of other entries in the SAME taskGroupId — unidirectional, so linking A→B never
   * writes B, and moveInboxEntryToTaskGroup clears this like every group-scoped field. */
  relatedInboxEntryIds?: string[];
  /** Hydrated form of `relatedInboxEntryIds`, populated by `getDetailedInboxEntry` only —
   * absent everywhere else, exactly like `communications` and `participants`. */
  relatedInboxEntries?: InboxEntry[];

  // Shared fields
  priority?: InboxEntryPriority | null;
  dueAt?: number | null;
  resolvedAt?: number | null;
  // The resolvedAt auto-resolve wrote when it closed a suggestion the user never
  // accepted. Only while it still equals resolvedAt does a reopen bring back the
  // suggestion; a later resolve by the user leaves it stale and the task theirs.
  autoResolvedSuggestionAt?: number | null;
  // The messages that system close rested on, meaningful only while
  // autoResolvedSuggestionAt equals resolvedAt: a reopen citing nothing newer
  // is refused. Readable: opaque messageIds, the sort key of
  // UserCommunicationsV2 (encryption rule reason 1), compared by id in the
  // reopen guard (reason 2).
  autoResolvedEvidenceIds?: string[] | null;
  // "Looks resolved": a message seemed to finish this accepted DoBox, and the
  // user confirms by resolving it. Readable: the ids are opaque messageIds, the
  // sort key of UserCommunicationsV2 (encryption rule reason 1), and carry no
  // content of the message.
  resolutionSuggestedAt?: number | null;
  resolutionEvidenceCommunicationIds?: string[] | null;
  // Evidence the user dismissed, so the same message never raises it again.
  // Readable for the same reason, and compared by id in suggestResolution's
  // condition, where ciphertext would never match.
  dismissedResolutionEvidenceIds?: string[];
  // Reply messages the owner dismissed, so they never light "New reply" again.
  // Readable for the same reason: the reply derivation compares them by id. A
  // DynamoDB string set, so it reads back as a Set; never sent to clients.
  dismissedReplyMessageIds?: string[] | Set<string>;
  // Ids in communicationIds that a weak writer linked (follow-up matcher,
  // continuation judge, auto-resolve evidence), which never put the entry in an
  // own pass's candidate set. Readable: opaque messageIds, the sort key of
  // UserCommunicationsV2 (encryption rule reason 1), with no message content.
  // A string set, written only as a merged-set SET inside the append's
  // versioned update.
  weakCommunicationIds?: string[] | Set<string>;
  // Keyed hmacEmail digests of the other people on the linked emails, kept by
  // the InboxEntries stream so auto-resolve can match a new email from another
  // thread. A DynamoDB string set, so it reads back as a Set; never sent to clients.
  counterpartEmailHmacs?: string[] | Set<string>;
  updatedAt: number;
  deletedAt?: number | null;
  lastModifiedByUserId?: string;
  lastModifiedByUserName?: string;

  // Origin
  source?: InboxEntrySource;
  // Set once at create, never updated. Readable: flowId is the partition key of
  // UserCommunicationAgents and executionId the key of FlowExecution, both read
  // by id to resolve the creator (encryption rule reason 1); no content.
  originFlowRef?: OriginFlowRef;
  /** Derived per viewer by the GraphQL resolver, never persisted. */
  originFlow?: InboxEntryOriginFlow | null;

  // Recurrence
  recurrenceInboxEntryId?: string;

  entryKind?: DoBoxEntryKind;
  flowApproval?: FlowApprovalRef;
  /** Terminal outcome of an APPROVAL_GATE entry; absent on entries decided before this field existed. */
  approvalDecision?: ApprovalDecision;
  agentDelegation?: InboxEntryAgentDelegation;
  /** Previous delegation lifecycles, newest first. Opened form of the sealed `agentRunHistoryJson`
   *  attribute; never written through the generic update path. */
  agentRunHistory?: AgentRunSummary[];
  /** SECONDARY runs, by `delegationId`; the primary stays in `agentDelegation`. Opened form: at rest
   *  each value is structural and its content rides in the sealed `agentRunsContentJson`. Written
   *  only by the per-run store primitives, never through the generic update path. */
  agentRuns?: Record<string, InboxEntryAgentDelegation>;
  /** Structured artifacts the ticket's runs produced, by artifact id. Opened form of the map plus
   *  the sealed `ticketArtifactsContentJson`. */
  ticketArtifacts?: Record<string, TicketArtifact>;

  // Cached doboxSuggestedActions result — regenerated whenever suggestedActionsFingerprint
  // no longer matches the task's current content. JSON-serialized DoBoxSuggestedAction[];
  // sealed at rest (names in `prompt`/`label` are user content), see INBOX_ENTRY_TARGET_FIELDS.
  suggestedActionsJson?: string;
  // SHA-256 digest of the task content the cached suggestions were generated from
  // (buildSuggestedActionsFingerprint). One-way hash, not reversible to the source content,
  // so — unlike suggestedActionsJson — it is never sealed; it exists only to detect a
  // changed task and is never rendered to a user.
  suggestedActionsFingerprint?: string;

  // Executor attention and approvals
  // Derived from executorAttentionReasons (EXECUTOR_ATTENTION_PRECEDENCE) on
  // the API — never stored. NONE means no reason currently applies.
  executorAttention?: ExecutorAttention;
  // Structural: readable DynamoDB string set (like counterpartEmailHmacs) —
  // which raw ExecutorAttentionReason(s) currently apply to this entry.
  executorAttentionReasons?: string[] | Set<string>;
  // Epoch ms the pending executor approval on this entry expires — timestamp
  // the executor branches on.
  executorApprovalExpiringAt?: number;

  // Doboxable signals (see doBoxSignals.ts)
  // Structural enum the engine branches on — mirrors DoBoxWorkKind.
  workKind?: DoBoxWorkKind;
  // Structural enum the engine branches on — mirrors DoBoxWaitingOn.
  waitingOn?: DoBoxWaitingOn;
  // UserCommunication sort key (messageId) the model identified as the
  // target thread — a hint for the executor to resolve against, never a
  // destination to send to directly.
  replyToCommunicationId?: string;
  // Keyed HMAC digest of the requester's identity — kept comparable so
  // route matching and outcome clustering can compare it without a decrypt.
  requesterHmac?: string;
  // Keyed HMAC digest of the source channel/thread identity — kept
  // comparable for the same reason as requesterHmac.
  sourceChannelKeyHmac?: string;
  // Structural boolean the engine branches on (auto-resolve settles it); the
  // namesakes themselves are recomputed from the channel cache, never stored.
  ambiguousAddressee?: boolean;

  // Sealed at rest (user-written model output) — SEALED. Capped, trimmed
  // missing-info items the model asked about, JSON-serialized.
  missingInfoJson?: string;
  // Sealed at rest (user-written content) — SEALED. The cached DoBox plan
  // the executor rendered, JSON-serialized.
  doboxPlanJson?: string;

  // Ready to send was removed. preparedPlanState/preparedOutcome are still
  // written by the undo path for an old already-sent PREPARED item
  // (utils/doboxExecutor/preparedOutcome.ts); nothing writes or reads them
  // otherwise, and the GraphQL resolvers no longer read them at all.
  preparedPlanState?: PreparedPlanState;
  preparedOutcome?: PreparedOutcome;

  // Cache bookkeeping — timestamps and digests the engine uses to decide
  // whether a cached result needs regenerating; never rendered to a user.
  suggestedActionsFailedAt?: number;
  suggestedActionsFailedFingerprint?: string;
  doboxPlanFingerprint?: string;
  doboxPlanFailedAt?: number;
  doboxPlanFailedFingerprint?: string;

  // Routing and links
  // Id of the AgentRoute the deterministic miner matched — never the model's
  // own choice.
  suggestedRouteId?: string;
  // Structural: which signal the route miner matched on. Plain string for
  // now — the Agent Routes feature has not shipped a dedicated enum yet.
  suggestedRouteMode?: string;
  // Id of the InboxEntry this entry originated from (e.g. an APPROVAL_GATE
  // entry's originating ticket) — readable ids only.
  originInboxEntryRef?: { taskGroupId: string; entryId: string };

  // Versioning and TTL
  version?: number;
  ttl?: number;
};

/** One proactive, directly-executable suggestion surfaced in the DoBox detail modal's
 *  Assistant panel empty state — see doboxSuggestedActions. */
export interface DoBoxSuggestedAction {
  label: string;
  prompt: string;
  isPrimary: boolean;
}

/** Presence-based unread tracking per user.
 * Record exists = user has NOT read the entry.
 * Record absent = user has read the entry (or was never a participant).
 */
export type InboxEntryUnread = {
  userId: string; // PK
  inboxEntryId: string; // SK
  taskGroupId: string; // Denormalized for grouping unread counts by task group
};

export enum DoboxConfigSource {
  EMAIL = 'email',
  CHAT = 'chat',
}

export interface CollectionMutation {
  add?: string[];
  remove?: string[];
}

export interface InboxEntryCollectionMutations {
  communicationIds?: CollectionMutation;
  taskLabelIds?: CollectionMutation;
  participantIds?: CollectionMutation;
  relatedInboxEntryIds?: CollectionMutation;
}

export type CreateInboxEntryInput = Partial<
  Omit<
    InboxEntry,
    | 'id'
    | 'createdFor'
    | 'createdAt'
    | 'updatedAt'
    | 'resolvedAt'
    | 'autoResolvedSuggestionAt'
    | 'autoResolvedEvidenceIds'
    | 'deletedAt'
    | 'version'
    | 'ttl'
    | 'participants'
    | 'communications'
    | 'isSuggestion'
    | 'checklists'
    | 'source'
    | 'resolutionSuggestedAt'
    | 'resolutionEvidenceCommunicationIds'
    | 'dismissedResolutionEvidenceIds'
    | 'dismissedReplyMessageIds'
    | 'weakCommunicationIds'
    | 'counterpartEmailHmacs'
    | 'monitorId'
    // Links are guarded in updateInboxEntry; only the internal create takes them, filtered by keepLiveRelatedTargets.
    | 'relatedInboxEntryIds'
    | 'relatedInboxEntries'
    // Executor-system-written only — never client-settable on create.
    | 'executorAttention'
    | 'executorAttentionReasons'
    | 'executorApprovalExpiringAt'
    | 'workKind'
    | 'waitingOn'
    | 'replyToCommunicationId'
    | 'requesterHmac'
    | 'sourceChannelKeyHmac'
    | 'ambiguousAddressee'
    | 'missingInfoJson'
    | 'doboxPlanJson'
    | 'preparedPlanState'
    | 'preparedOutcome'
    | 'suggestedActionsFailedAt'
    | 'suggestedActionsFailedFingerprint'
    | 'doboxPlanFingerprint'
    | 'doboxPlanFailedAt'
    | 'doboxPlanFailedFingerprint'
    | 'suggestedRouteId'
    | 'suggestedRouteMode'
    | 'originInboxEntryRef'
    | 'originFlowRef'
    | 'originFlow'
    | 'agentRunHistory'
    | 'agentRuns'
    | 'ticketArtifacts'
  >
> &
  Required<Pick<InboxEntry, 'subject'>>;

export type InternalCreateInboxEntryInput = CreateInboxEntryInput &
  Pick<
    InboxEntry,
    | 'isSuggestion'
    | 'checklists'
    | 'relatedInboxEntryIds'
    | 'workKind'
    | 'waitingOn'
    | 'replyToCommunicationId'
    | 'requesterHmac'
    | 'sourceChannelKeyHmac'
    | 'ambiguousAddressee'
    | 'missingInfoJson'
    | 'suggestedRouteId'
    | 'suggestedRouteMode'
    | 'originInboxEntryRef'
    | 'originFlowRef'
  > &
  Required<Pick<InboxEntry, 'source'>>;

export type UpdateInboxEntryInput = Partial<
  Omit<
    InboxEntry,
    | 'id'
    | 'createdFor'
    | 'createdAt'
    | 'updatedAt'
    | 'deletedAt'
    | 'version'
    | 'ttl'
    | 'participantIds'
    | 'participants'
    | 'communicationIds'
    | 'communications'
    | 'taskLabelIds'
    | 'taskGroupId'
    | 'checklists'
    | 'relatedInboxEntryIds'
    | 'relatedInboxEntries'
    | 'resolutionSuggestedAt'
    | 'resolutionEvidenceCommunicationIds'
    | 'dismissedResolutionEvidenceIds'
    | 'dismissedReplyMessageIds'
    | 'weakCommunicationIds'
    | 'counterpartEmailHmacs'
    // Executor-system-written only — never client-settable on update.
    | 'executorAttention'
    | 'executorAttentionReasons'
    | 'executorApprovalExpiringAt'
    | 'workKind'
    | 'waitingOn'
    | 'replyToCommunicationId'
    | 'requesterHmac'
    | 'sourceChannelKeyHmac'
    | 'ambiguousAddressee'
    | 'missingInfoJson'
    | 'doboxPlanJson'
    | 'preparedPlanState'
    | 'preparedOutcome'
    | 'suggestedActionsFailedAt'
    | 'suggestedActionsFailedFingerprint'
    | 'doboxPlanFingerprint'
    | 'doboxPlanFailedAt'
    | 'doboxPlanFailedFingerprint'
    | 'suggestedRouteId'
    | 'suggestedRouteMode'
    | 'originInboxEntryRef'
    | 'originFlowRef'
    | 'originFlow'
    | 'monitorId'
    | 'agentRunHistory'
    | 'agentRuns'
    | 'ticketArtifacts'
  >
> &
  Required<Pick<InboxEntry, 'id'>>;

export type UnreadInboxEntriesByTaskGroup = {
  taskGroupId: string;
  inboxEntryIds: string[];
};

export interface InboxEntryOrderKeyAssignment {
  inboxEntryId: string;
  orderKey: string;
}
